import React, { memo, useEffect, useState, useCallback, useMemo } from 'react'
import get from 'lodash/get'
import isString from 'lodash/isString'
import { useGeocodingSearch } from 'transfix-maps/hooks/useGeocodingSearch'
import { getTruncatedPostalCode } from 'transfix-maps/utils/getTruncatedPostalCode'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
  FilterOptionType,
} from 'transfix-ui/components/Autosuggest'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IGeocodingResultTypeEnum, IGeocodingAllowedCountryEnum } from 'types/graphqlTypes'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useInputState } from 'utils/hooks/useInputState'
import { v4 as uuid } from 'uuid'
import { getEntityById } from 'utils/getEntityById'

/**
 * Generate a string to use as a label from mapbox feature data
 * @param {IMapboxFeature} feature - Object to be formatted
 * @return {string} i.e. 'New York, NY'
 * @deprecated Please delete asap
 */
const getMapboxFeatureLabel = ({
  addressLine1,
  city,
  country,
  postalCode,
  state,
}: ILocationSuggestion): string => {
  let label = ''
  if (addressLine1) label += `${addressLine1}, `
  if (city) label += `${city}, `
  if (state) label += `${state}`
  if (postalCode) label += `, ${postalCode}`
  if (country) label += `, ${country}`

  // Removes the final comma and space, if there is one
  return label.replace(/(,\s)$/, '')
}

const DEFAULT_COUNTRIES = [IGeocodingAllowedCountryEnum.US]

/**
 * @example getShortAddress('123 Broadway, Brooklyn, New York, 11201, United States') // '123 Broadway'
 */
const getShortAddress = (address: string | undefined): string => {
  const INDICES_FROM_END = 4
  const items = (address || '').split(', ')

  /**
   * This is a crude way of getting "the 4th item from the end" since we don't
   * have total control over these string formats, but they are consistent.
   */
  const cityIndex = items.length - INDICES_FROM_END
  return items.slice(0, cityIndex).join(', ')
}

/**
 * Returns a substring of a string, by using a property from the source object.
 * Kind of like a reverse map function.
 * @example
 * ```
 * const facilityName = parseInputValue(
 *  {
 *    id: '123',
 *    facilityName: 'Cool Facility',
 *    fullLabel: 'Cool Facility 2000, Goshen, NY 10924, United State'
 *  },
 *  'Cool Facility 2000, Goshen, NY 10924, United State', // unfiltered text from input
 *  'facilityName' // key name
 * )
 * // returns 'Cool Facility', which is `name` prop
 * ```
 */
export function parseInputValue<T extends object>(
  sourceObj: T | undefined,
  inputVal: string = '',
  keyName: keyof T
): string {
  const value = get(sourceObj, keyName, '')

  // If it's not a valid value, return empty string
  if (!isString(value) || !value.length) return inputVal

  const startIndex = inputVal?.indexOf(value) || 0
  const endIndex = startIndex + value?.length
  return inputVal.substring(startIndex, endIndex)
}

type RequiredAddressType =
  | 'id'
  | 'city'
  | 'state'
  | 'addressLine1'
  | 'addressLine2'
  | 'label'
  | 'name'
  | 'postalCode'
  | 'countryCode'

/**
 * This type ensures that 'id', 'city', 'label', 'state',
 * 'addressLine1', 'name' are all defined.
 *
 * All of these are necessary to display and select a valid location.
 */

export interface IAddressAutosuggestValue
  extends Required<Pick<ILocationSuggestion, RequiredAddressType>> {}

export interface IAddressAutosuggestProps
  extends IAutosuggestWrapperProps<IAddressAutosuggestValue> {
  clearable?: boolean
  clearableWhenNotFocused?: boolean
  /**
   * @default [IGeocodingAllowedCountryEnum.US]
   */
  countries?: IGeocodingAllowedCountryEnum[]
  /**
   * Filter applied to AddressAutosuggest results
   * @default "showAll"
   */
  filterOptionsType?: FilterOptionType
}

export const AddressAutosuggest = memo<IAddressAutosuggestProps>(
  ({
    clearable = true,
    clearableWhenNotFocused = true,
    countries = DEFAULT_COUNTRIES,
    dataTest = 'address-autosuggest',
    disabled: disabledProp,
    error: errorProp,
    filterOptionsType = 'showAll',
    icon,
    id: idProp = 'location',
    label: labelProp = 'Address Line 1',
    large = true,
    onAddEntity: onAddEntityProp,
    onBlur: onBlurProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
    placeholder: placeholderProp = 'Begin typing an address...',
    required: requiredProp,
    value,
  }) => {
    const { isMobile } = useBreakpointContext()

    // Mirrors actual value, but triggers API calls and can be used for validation
    const [inputValue, setInputValue] = useState<string>('')

    const { isDirty, isFocused, onBlurTrigger, onFocusTrigger } = useInputState()

    const [isClearable, setIsClearable] = useState<boolean>(
      clearable && (clearableWhenNotFocused ? true : isFocused)
    )

    // Watch the isFocused state to add or remove the clear button
    useEffect(() => {
      if (clearable) {
        if (!clearableWhenNotFocused && !isFocused) {
          setIsClearable(false)
        } else {
          setIsClearable(true)
        }
      }
    }, [clearable, clearableWhenNotFocused, isFocused])

    const { results: mapResults = [], getResults } = useGeocodingSearch({
      debounce: 250,
      getResultLabel: ({ address }) => {
        return address ?? ''
      },
      resultTypes: [IGeocodingResultTypeEnum.Address, IGeocodingResultTypeEnum.POI],
      countries,
    })

    const results: ILocationSuggestion[] = useMemo(() => {
      return [...mapResults].map(result => {
        const { addressLine1, stateCode } = result || {}
        return {
          ...result,
          addressLine1: getShortAddress(addressLine1),
          name: '',
          state: stateCode ?? '',
        }
      })
    }, [mapResults])

    if (value?.addressLine1 && !results.length && !isFocused && !isDirty) {
      value.id = value.id ?? uuid()
      results.unshift({
        ...value,
        label: getMapboxFeatureLabel(value),
      })
    }

    // Update query search when input updates
    useEffect(() => {
      async function fetchResults() {
        await getResults(inputValue)
      }

      // Prevents fetch on mount
      if (!!inputValue && inputValue !== value?.label) {
        fetchResults()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue])

    // This formats the options
    const options = getAutosuggestOptions({
      options: results,
      groupIndex: 1,
      groupName: 'Location',
      iconName: 'LocationPinIcon',
      inputValue,
      mapOption: ({ id = '', label: text = '' }) => ({ id, text }),
    })

    /**
     * When a selection is clicked, trigger `changeHandler`
     */
    const changeHandler = useCallback(
      (option: IAutocompleteOption | undefined) => {
        const location = getEntityById(results, option?.id)
        const { countryCode, postalCode: postalCodeFull, stateCode: state } = location || {}
        const postalCode = getTruncatedPostalCode(postalCodeFull, { countryCode })
        onChangeProp({
          ...location,
          state,
          postalCode,
        })
      },
      [onChangeProp, results]
    )

    const inputChangeHandler = useCallback(
      (val: string = '') => {
        setInputValue(val)

        // If the input changes, we use the value to set `addressLine1`
        onChangeProp({
          addressLine1: val,
        })
      },
      [onChangeProp]
    )

    /**
     * Returns a substring of the full label for customized input value
     */
    const parseInputValueFunc = useCallback(
      (inputString: string) => {
        return parseInputValue(value, inputString, 'addressLine1')
      },
      [value]
    )

    const focusHandler = useCallback(() => onFocusTrigger(), [onFocusTrigger])

    const clearHandler = useCallback(() => {
      onChangeProp({
        addressLine1: '',
      })
    }, [onChangeProp])

    /**
     * Make sure the useInputState blur/focused state is updated when input is blurred.
     * Call the onBlurProp if provided
     */
    const blurHandler = useCallback(
      (e: React.FocusEvent<HTMLInputElement, Element>) => {
        onBlurTrigger()
        if (onBlurProp) {
          onBlurProp(e)
        }
      },
      [onBlurProp, onBlurTrigger]
    )

    const id = useMemo(() => [idProp, 'search'].join('-'), [idProp])

    return (
      <Autosuggest
        id={id}
        label={labelProp}
        placeholder={placeholderProp}
        onAddEntity={onAddEntityProp}
        value={value}
        options={options}
        onCancel={onCancelProp}
        onChange={changeHandler}
        onInputChange={inputChangeHandler}
        renderOptionType='withIcon'
        icon={icon}
        clearable={isClearable}
        dataTest={dataTest}
        error={errorProp}
        onBlur={blurHandler}
        disabled={disabledProp}
        filterOptionsType={filterOptionsType}
        selectOnFocus={!isMobile}
        parseInputValue={parseInputValueFunc}
        required={requiredProp}
        renderGroupType='withoutGroupName'
        onFocus={focusHandler}
        onClearButtonClick={clearHandler}
        large={large}
        freeSolo
      />
    )
  }
)

export default AddressAutosuggest
